// Enhanced cache busting utility for UniChat
// Handles both localStorage and browser cache issues

/**
 * Add cache busting parameters to URLs
 */
export const addCacheBustingParams = (url: string): string => {
  const timestamp = Date.now();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${timestamp}`;
};

/**
 * Get current app version for cache busting
 */
export const getAppVersion = (): string => {
  // Use build timestamp as primary version identifier
  const buildTimestamp = process.env.REACT_APP_BUILD_TIMESTAMP;
  const packageVersion = process.env.REACT_APP_VERSION || process.env.npm_package_version;
  
  // If we have a build timestamp, use it for more reliable cache busting
  if (buildTimestamp) {
    return `${packageVersion || '1.0.0'}-${buildTimestamp}`;
  }
  
  // Use a static version for development/builds without timestamp
  // This prevents continuous version change detection
  return `${packageVersion || '1.0.0'}-dev`;
};

/**
 * Enhanced cache clearing that handles both localStorage and browser cache
 */
export const checkVersionAndClearCache = (): void => {
  const currentVersion = localStorage.getItem('unichat_app_version');
  const newVersion = getAppVersion();
  
  if (currentVersion !== newVersion) {
    console.log('🔄 Version changed, clearing cache...');
    console.log(`Previous version: ${currentVersion}, New version: ${newVersion}`);
    
    // Clear localStorage except for conversation history
    const preservedKeys = [
      'unichat_current_conversation',
      'unifirst-conversation-favorites',
      'unichat_user_preferences',
      'unichat_appearance_settings',
      'unichat_streaming_preference',
      'unichat_layout_settings'
    ];
    
    const allKeys = Object.keys(localStorage);
    for (const key of allKeys) {
      if (!preservedKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    }
    
    // Update version
    localStorage.setItem('unichat_app_version', newVersion);
    
    // Handle cache refresh based on version change
    handleCacheRefresh(currentVersion, newVersion);
    
    console.log('✅ Cache cleared while preserving conversation history');
  }
};

/**
 * Check if a hard refresh is needed based on version change severity
 */
export const shouldForceHardRefresh = (oldVersion: string | null, newVersion: string): boolean => {
  if (!oldVersion) return false;
  
  // Extract version parts (ignoring timestamp)
  const oldVersionBase = oldVersion.split('-')[0];
  const newVersionBase = newVersion.split('-')[0];
  
  // Force hard refresh for any version change, not just major
  return oldVersionBase !== newVersionBase;
};

/**
 * Enhanced cache refresh strategy
 */
export const handleCacheRefresh = (oldVersion: string | null, newVersion: string): void => {
  console.log('🔄 Handling cache refresh...', { oldVersion, newVersion });
  
  // For any version change, show a user-friendly update notification
  if (oldVersion && oldVersion !== newVersion) {
    // Clear service worker cache if available
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      });
    }
    
    // Clear browser cache storage APIs
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
      });
    }
    
    // Show update notification and reload
    const shouldReload = window.confirm(
      '🆕 A new version of the application is available!\n\n' +
      'Click OK to refresh and get the latest features and fixes.'
    );
    
    if (shouldReload) {
      // Force a hard reload that bypasses cache
      window.location.reload();
    }
  }
}; 